import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import {ProteinAlignment} from 'constants/AlignmentConstants';
import FieldActionButtons from 'metadata/FieldActionButtons';
import AaSequenceAlignmentActions from 'molbio/aa-sequences/alignments/AaSequenceAlignmentActions';
import {AaSequenceAlignmentSummary} from 'molbio/aa-sequences/util/AaSequenceConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import FormInput from 'ui-library/bs/FormInput';
import SortableTable, {SortableTableColumn} from 'ui-library/SortableTable';
import {readableTime} from 'util/DateUtil';
import * as keyboard from 'util/Keyboard';
import * as modals from 'util/Modals';
import {ElementKeyboardEvent, ElementMouseEvent, InputChangeEvent, InputFocusEvent} from 'util/Types';

type AaSequenceTableAlignment = ProteinAlignment | AaSequenceAlignmentSummary;
interface AaSequenceAlignmentPanelTableProps {
  alignments: Array<AaSequenceTableAlignment>;
  closePanel: () => void;
  showActionButtons: boolean;
  dataTestAttrs?: DataTestAttrs;
}

interface AaSequenceAlignmentPanelTableState {
  rowUiState: {[alignmentId: string]: AlignmentRowUIState};
}

interface AlignmentRowUIState {
  isEditing: boolean;
  inputValue: string;
}

class AaSequenceAlignmentPanelTable extends React.Component<
  AaSequenceAlignmentPanelTableProps,
  AaSequenceAlignmentPanelTableState
> {
  override UNSAFE_componentWillReceiveProps(newProps: AaSequenceAlignmentPanelTableProps): void {
    this.setState({
      rowUiState: this._mapAlignments(
        newProps.alignments,
        (alignment) => this.state.rowUiState[alignment.id] || this._emptyRowUiState(alignment)
      ),
    });
  }

  _mapAlignments = <T,>(
    alignments: Array<AaSequenceTableAlignment>,
    func: (alignment: AaSequenceTableAlignment) => T
  ): {[alignmentId: string]: T} =>
    _.object(
      // eslint-disable-next-line no-restricted-syntax
      _.pluck(alignments, 'id'),
      alignments.map((alignment) => func(alignment))
    );

  _emptyRowUiState = (alignment: AaSequenceTableAlignment): AlignmentRowUIState => ({
    isEditing: false,
    inputValue: alignment.name,
  });

  _isNotEditingAnyRow = (): boolean =>
    !Object.values(this.state.rowUiState).some((rowState) => rowState.isEditing);

  _handleClickRow = (alignment: AaSequenceTableAlignment): void => {
    if (this._isNotEditingAnyRow()) {
      AaSequenceAlignmentActions.open({proteinId: alignment.proteinId, alignmentId: alignment.id});
      this.props.closePanel();
    }
  };

  _deleteAlignmentHandler =
    (alignment: AaSequenceTableAlignment) =>
    (e: ElementMouseEvent): void => {
      // Prevent row from being clicked
      e.stopPropagation();
      modals
        .confirm(
          'Confirm Delete',
          <span>
            Are you sure you want to delete the alignment <strong>{alignment.name}</strong>? This action is
            not reversible.
          </span>,
          'danger'
        )
        .done(() => {
          AaSequenceAlignmentActions.delete(alignment.proteinId, alignment.id);
        });
    };

  _getInputValue = (alignmentId: string): string => this.state.rowUiState[alignmentId].inputValue;

  _getIsEditing = (alignmentId: string): boolean => this.state.rowUiState[alignmentId].isEditing;

  _setRowUiState = (alignmentId: string, updateObject: Partial<AlignmentRowUIState>): void => {
    const newState = reactUpdate(this.state, {
      rowUiState: {
        [alignmentId]: {$merge: updateObject},
      },
    });
    this.setState(newState);
  };

  _editHandler =
    (alignmentId: string) =>
    (e: ElementMouseEvent): void => {
      e.stopPropagation();
      this._setRowUiState(alignmentId, {isEditing: true});
    };

  _cancelHandler = (alignmentId: string, alignmentName: string) => (): void => {
    this._setRowUiState(alignmentId, {
      isEditing: false,
      inputValue: alignmentName,
    });
  };

  _completeEditing = (alignment: AaSequenceTableAlignment): void => {
    const name = this._getInputValue(alignment.id);
    AaSequenceAlignmentActions.rename(alignment.id, name);
    this._setRowUiState(alignment.id, {isEditing: false});
  };

  _completeEditingHandler = (alignment: AaSequenceTableAlignment) => (): void => {
    this._completeEditing(alignment);
  };

  _isCompleteEnabled = (alignmentId: string): boolean => Boolean(this._getInputValue(alignmentId));

  _onChangeHandler =
    (alignmentId: string) =>
    (e: InputChangeEvent): void => {
      this._setRowUiState(alignmentId, {inputValue: e.target.value});
    };

  _keyDownHandler =
    (alignment: AaSequenceTableAlignment) =>
    (e: ElementKeyboardEvent): void => {
      if (keyboard.isEnterKey(e)) {
        e.preventDefault();
        if (this._isCompleteEnabled(alignment.id)) {
          this._completeEditing(alignment);
        }
      } else if (keyboard.isEscapeKey(e)) {
        e.preventDefault();
        e.stopPropagation();
        this._setRowUiState(alignment.id, {isEditing: false});
      }
    };

  _handleOnFocus = (e: InputFocusEvent): void => {
    const input = e.target;
    input.setSelectionRange(0, input.value.length);
  };

  _renderNameColumn = (column: SortableTableColumn, row: AaSequenceTableAlignment): JSX.Element | string => {
    const alignmentId = row.id;
    if (this._getIsEditing(alignmentId)) {
      return (
        <FormInput
          autoFocus={true}
          onFocus={this._handleOnFocus}
          value={this._getInputValue(alignmentId)}
          onChange={this._onChangeHandler(alignmentId)}
          onKeyDown={this._keyDownHandler(row)}
        />
      );
    } else {
      return row.name;
    }
  };

  _renderActionButtonColumn = (column: SortableTableColumn, row: AaSequenceTableAlignment): JSX.Element => {
    const alignmentId = row.id;
    return this.props.showActionButtons ? (
      <FieldActionButtons
        isEditing={this._getIsEditing(alignmentId)}
        cancel={this._cancelHandler(alignmentId, row.name)}
        complete={this._completeEditingHandler(row)}
        isCompleteEnabled={this._isCompleteEnabled(alignmentId)}
        remove={this._deleteAlignmentHandler(row)}
        edit={this._editHandler(alignmentId)}
        className="alignmentPanel-actionButtons"
      />
    ) : (
      <></>
    );
  };

  override state: AaSequenceAlignmentPanelTableState = {
    rowUiState: this._mapAlignments(this.props.alignments, this._emptyRowUiState),
  };

  override render(): JSX.Element {
    return (
      <SortableTable
        columns={[
          SortableTable.defaultColumn('Name', 'name', {render: this._renderNameColumn}),
          SortableTable.defaultColumn('Created', 'created', {
            render: (column, row: AaSequenceTableAlignment) => readableTime(row.createdAt, {showTime: false}),
          }),
          SortableTable.defaultColumn('', '', {
            header: '',
            sortable: false,
            render: this._renderActionButtonColumn,
          }),
        ]}
        rows={this.props.alignments}
        onClickRow={this._handleClickRow}
        clickable={this._isNotEditingAnyRow()}
        verticalAlignMiddle={true}
        className="u-small-spacing-top panel-container-table alignmentPanel-table"
        dataTestAttrs={this.props.dataTestAttrs}
      />
    );
  }
}

export default AaSequenceAlignmentPanelTable;
